/**
 * Balance Calculator Utility
 * 
 * Pure utility functions for calculating transaction impacts on account balances.
 * Handles total impact calculation, balance deltas for updates, and validation.
 */

export interface BalanceImpact {
  totalImpact: number;
  direction: 'increase' | 'decrease';
}

export interface BalanceValidation {
  isValid: boolean;
  currentBalance: number;
  requiredAmount: number;
  resultingBalance: number;
  error?: BalanceValidationError;
}

export interface BalanceValidationError {
  message: string;
  currentBalance: number;
  requiredAmount: number;
  shortfall: number;
  currency: string;
}

/**
 * Calculate the total impact of a transaction on account balance.
 * 
 * For expenses: impact = -(amount + fee)
 * For income: impact = +(amount - fee), since the fee is withheld from what is credited
 * 
 * @param amount - Transaction amount (always positive)
 * @param fee - Transaction fee (null treated as 0)
 * @param type - Transaction type ('income' or 'expense')
 * @returns BalanceImpact with totalImpact and direction
 */
export function calculateTotalImpact(
  amount: number,
  fee: number | null,
  type: 'income' | 'expense'
): BalanceImpact {
  const feeAmount = fee ?? 0;
  const totalImpact = type === 'expense' ? amount + feeAmount : amount - feeAmount;
  
  return {
    totalImpact,
    direction: type === 'expense' ? 'decrease' : 'increase'
  };
}

/**
 * Calculate the balance delta when updating a transaction.
 * 
 * Delta represents the net change to apply to the account balance.
 * Positive delta = balance should increase
 * Negative delta = balance should decrease
 * 
 * @param oldAmount - Original transaction amount
 * @param oldFee - Original transaction fee (null treated as 0)
 * @param newAmount - Updated transaction amount
 * @param newFee - Updated transaction fee (null treated as 0)
 * @param type - Transaction type ('income' or 'expense')
 * @returns Balance delta to apply
 */
export function calculateBalanceDelta(
  oldAmount: number,
  oldFee: number | null,
  newAmount: number,
  newFee: number | null,
  type: 'income' | 'expense'
): number {
  const oldTotal = calculateTotalImpact(oldAmount, oldFee, type).totalImpact;
  const newTotal = calculateTotalImpact(newAmount, newFee, type).totalImpact;
  const delta = newTotal - oldTotal;
  
  // For expenses, increasing amount means decreasing balance (negative delta)
  // For income, increasing amount means increasing balance (positive delta)
  return type === 'expense' ? -delta : delta;
}

/**
 * Validate if an operation would result in sufficient balance.
 * 
 * For expense transactions, ensures the resulting balance would not be negative.
 * For income transactions, always returns valid.
 * 
 * @param currentBalance - Current account balance
 * @param impact - Total impact amount (always positive)
 * @param type - Transaction type ('income' or 'expense')
 * @param currency - Account currency for error messages
 * @param operation - Operation type for error context
 * @returns BalanceValidation result
 */
export function validateSufficientBalance(
  currentBalance: number,
  impact: number,
  type: 'income' | 'expense',
  currency: string,
  operation: 'create' | 'update' = 'create'
): BalanceValidation {
  // Income transactions always allowed
  if (type === 'income') {
    return {
      isValid: true,
      currentBalance,
      requiredAmount: impact,
      resultingBalance: currentBalance + impact
    };
  }
  
  // For expenses, check if balance would go negative
  const resultingBalance = currentBalance - impact;
  
  if (resultingBalance < 0) {
    const shortfall = impact - currentBalance;
    const operationText = operation === 'create' ? 'transaction' : 'update';
    
    return {
      isValid: false,
      currentBalance,
      requiredAmount: impact,
      resultingBalance,
      error: {
        message: `Insufficient balance: this ${operationText} would result in a negative account balance`,
        currentBalance,
        requiredAmount: impact,
        shortfall,
        currency
      }
    };
  }
  
  return {
    isValid: true,
    currentBalance,
    requiredAmount: impact,
    resultingBalance
  };
}
